import { Typography, Icon, BaseType } from '@globalmed/atlas-ui';
import { ReactNode } from 'react';
import cn from 'classnames';
import styles from './SelectableList.module.css';

type TSelectableListItem = BaseType & {
  content?: ReactNode;
  disabled?: boolean;
};

type TProps = {
  items: TSelectableListItem[];
  selected: string | string[];
  onChange: (id: string) => void;
  'aria-label'?: string;
};

/**
 * Renders a selectable list of items.
 * Displays a check icon next to each selected item.
 * Supports both simple text items (via `title`) and custom React content (via `content`).
 * @param props - Component props.
 * @param props.items - Array of option objects containing `id`, and either `title` (for text) or `content` (for custom React node), and optional `image` and `disabled`.
 * @param props.selected - The `id` of the selected item, or the ids of several selected items.
 * @param props.onChange - Callback function called with the `id` of the clicked item.
 * @param props.aria-label - Accessible name announced for the group of options.
 * @returns JSX.Element representing a selectable list.
 */
export const SelectableList = ({
  items,
  selected,
  onChange,
  'aria-label': ariaLabel,
}: TProps) => {
  return (
    <div className={styles.listContainer} role="group" aria-label={ariaLabel}>
      {items.map((item) => {
        const isSelected = Array.isArray(selected)
          ? selected.includes(item.id)
          : item.id === selected;

        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={isSelected}
            disabled={item.disabled}
            className={cn(styles.listItem, {
              [styles.active]: isSelected,
            })}
            onClick={() => onChange(item.id)}
          >
            {item.image && (
              <img
                src={item.image}
                alt={item.title ?? ''}
                className={styles.itemImage}
              />
            )}
            {item.content ? (
              item.content
            ) : (
              <Typography>{item.title}</Typography>
            )}
            {isSelected && (
              <Icon
                sx={{ marginLeft: 'auto' }}
                color={'var(--colors-primary600)'}
                name="Check"
                decorative
              />
            )}
          </button>
        );
      })}
    </div>
  );
};
